import {
  BarChart3,
  BookOpenText,
  ClipboardCheck,
  Database,
  DatabaseZap,
  GitCompareArrows,
  Gauge,
  FlaskConical,
  History,
  Info,
  LayoutDashboard,
  Landmark,
  ListChecks,
  Map,
  MapPinned,
  Menu,
  Orbit,
  SlidersHorizontal,
  Sparkles,
  Trophy,
  UsersRound,
  Waves,
  Crosshair,
  CircleHelp,
  X,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { api } from '../api/client'
import { appLanguage, changeAppLanguage } from '../i18n'
import { LanguageSwitcher } from './LanguageSwitcher'
import { StatusPill } from './StatusPill'
import { ThemeSwitcher } from './ThemeSwitcher'

interface NavItem {
  to: string
  key: string
  icon: typeof LayoutDashboard
  end?: boolean
}

const RESULT_NAV_ITEMS: NavItem[] = [
  { to: '/', key: 'overview', icon: LayoutDashboard, end: true },
  { to: '/explorar', key: 'explore', icon: Map },
  { to: '/icm-numerico', key: 'numericIcm', icon: Gauge },
  { to: '/comparar', key: 'compare', icon: GitCompareArrows },
  { to: '/segmentacao', key: 'segmentation', icon: UsersRound },
  { to: '/experimentos', key: 'experiments', icon: FlaskConical },
  { to: '/revisao-fontes/casa-civil/ranks', key: 'casaCivilRankAtlas', icon: Trophy },
  { to: '/casa-civil', key: 'casaCivil', icon: Landmark },
  { to: '/listas', key: 'lists', icon: ListChecks },
]

const LAB_NAV_ITEMS: NavItem[] = [
  { to: '/laboratorio', key: 'scenario', icon: Orbit },
  { to: '/laboratorio-cortes', key: 'cutoffLab', icon: Crosshair },
]

const PNA_NAV_ITEMS: NavItem[] = [
  { to: '/mc-pna-ana', key: 'pnaAnaAtlas', icon: MapPinned, end: true },
  { to: '/mc-pna-ana/classes', key: 'pnaAnaClasses', icon: Waves },
  { to: '/mc-pna-ana/robustez', key: 'pnaAnaRobustness', icon: SlidersHorizontal },
]

const METHODOLOGY_NAV_ITEMS: NavItem[] = [
  { to: '/revisao-fontes', key: 'sourceRevision', icon: DatabaseZap, end: true },
  { to: '/revisao-fontes/calibracao-icm', key: 'icmCalibration', icon: SlidersHorizontal },
  { to: '/dados', key: 'data', icon: Database },
  { to: '/guia-analises', key: 'analysisGuide', icon: CircleHelp },
  { to: '/casa-civil/revisao', key: 'auditReview', icon: ClipboardCheck },
  { to: '/revisao-fontes/casa-civil', key: 'sourceRevisionCivil', icon: Landmark, end: true },
  { to: '/sensibilidade', key: 'analytics', icon: SlidersHorizontal },
  { to: '/metodologia', key: 'methodology', icon: BookOpenText },
]

const SUPPORT_NAV_ITEMS: NavItem[] = [
  { to: '/execucoes', key: 'executions', icon: History },
  { to: '/sobre', key: 'about', icon: Info },
]

function NavItems({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  const { t } = useTranslation()
  return items.map(({ to, key, icon: Icon, end }) => (
    <NavLink
      key={to}
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-lg px-3 py-2 text-[12px] font-medium ${
          isActive
            ? 'bg-white/12 text-white shadow-[inset_3px_0_0_#f6a723]'
            : 'text-side-dim hover:bg-white/7 hover:text-side-ink'
        }`
      }
    >
      <Icon size={16} aria-hidden="true" />
      {t(`nav.${key}`)}
    </NavLink>
  ))
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation()
  return (
    <aside className="sidebar-gradient fixed inset-y-0 left-0 z-40 flex w-[244px] flex-col text-side-ink print:hidden">
      <div className="flex h-[84px] items-center gap-3 border-b border-white/10 px-5">
        <div className="h-12 w-12 shrink-0">
          <img src="/brand/logo-sedec.png" className="brand-sedec h-12 w-12 object-contain" alt={t('shell.logoAlt')} />
          <div className="brand-onda h-12 w-12 items-center justify-center rounded-xl border border-white/15 bg-white/8 text-[#7fc8bb]" aria-hidden="true">
            <Sparkles size={23} />
          </div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-side-dim">SEDEC · DPM</div>
          <div className="mt-0.5 text-xl font-semibold tracking-[-0.02em]">ICPM</div>
        </div>
      </div>

      <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-4" aria-label={t('nav.main')}>
        <div className="space-y-1">
          <NavItems items={RESULT_NAV_ITEMS} onNavigate={onNavigate} />
        </div>
        <section className="rounded-xl border border-white/15 bg-black/10 p-2" aria-labelledby="lab-nav-title">
          <h2 id="lab-nav-title" className="px-2 pb-1 pt-1 text-[9px] font-bold uppercase tracking-[0.18em] text-[#f6c260]">
            {t('nav.laboratoryGroup')}
          </h2>
          <div className="space-y-1"><NavItems items={LAB_NAV_ITEMS} onNavigate={onNavigate} /></div>
        </section>
        <section className="rounded-xl border border-[#db05a6]/45 bg-[#db05a6]/8 p-2" aria-labelledby="pna-nav-title">
          <h2 id="pna-nav-title" className="px-2 pb-1 pt-1 text-[9px] font-bold uppercase tracking-[0.18em] text-[#ff83db]">
            {t('nav.pnaAnaGroup')}
          </h2>
          <p className="px-2 pb-2 text-[9px] leading-4 text-side-dim">{t('nav.pnaAnaGroupDetail')}</p>
          <div className="space-y-1"><NavItems items={PNA_NAV_ITEMS} onNavigate={onNavigate} /></div>
        </section>
        <section className="rounded-xl border border-white/15 bg-black/10 p-2" aria-labelledby="method-nav-title">
          <h2 id="method-nav-title" className="px-2 pb-1 pt-1 text-[9px] font-bold uppercase tracking-[0.18em] text-[#7fc8bb]">
            {t('nav.methodologyGroup')}
          </h2>
          <p className="px-2 pb-2 text-[9px] leading-4 text-side-dim">{t('nav.methodologyGroupDetail')}</p>
          <div className="space-y-1"><NavItems items={METHODOLOGY_NAV_ITEMS} onNavigate={onNavigate} /></div>
        </section>
        <div className="space-y-1 border-t border-white/10 pt-3">
          <NavItems items={SUPPORT_NAV_ITEMS} onNavigate={onNavigate} />
        </div>
      </nav>

      <div className="border-t border-white/10 p-4">
        <div className="rounded-xl bg-black/10 p-3">
          <div className="flex items-center gap-2 text-xs font-semibold">
            <BarChart3 size={15} className="text-[#f6a723]" />
            {t('shell.methodology')}
          </div>
          <p className="mt-1 text-[10px] leading-4 text-side-dim">{t('shell.methodologyDetail')}</p>
        </div>
      </div>
    </aside>
  )
}

export function AppShell() {
  const { t, i18n } = useTranslation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const run = useQuery({ queryKey: ['current-run'], queryFn: ({ signal }) => api.currentRun(signal) })
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const urlLanguage = params.get('lang')
    const explicitLanguage = urlLanguage === 'en'
      ? 'en'
      : urlLanguage === 'pt' || urlLanguage === 'pt-BR'
        ? 'pt-BR'
        : null

    if (explicitLanguage && explicitLanguage !== appLanguage()) {
      document.documentElement.lang = explicitLanguage
      void changeAppLanguage(explicitLanguage)
      return
    }

    const language = appLanguage()
    document.documentElement.lang = language
    if (language === 'en' && urlLanguage !== 'en') {
      params.set('lang', 'en')
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true })
    } else if (language === 'pt-BR' && urlLanguage) {
      params.delete('lang')
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true })
    }
  }, [i18n.resolvedLanguage, location.pathname, location.search, navigate])

  useEffect(() => {
    const title = t('shell.documentTitle')
    const description = t('shell.documentDescription')
    document.title = title
    document.querySelector('meta[name="description"]')?.setAttribute('content', description)
    document.querySelector('meta[property="og:title"]')?.setAttribute('content', title)
    document.querySelector('meta[property="og:description"]')?.setAttribute('content', description)
    document.querySelector('meta[name="twitter:title"]')?.setAttribute('content', title)
    document.querySelector('meta[name="twitter:description"]')?.setAttribute('content', description)
    document.querySelector('meta[property="og:locale"]')?.setAttribute(
      'content',
      appLanguage() === 'en' ? 'en_US' : 'pt_BR',
    )

    const params = new URLSearchParams(location.search)
    params.delete('lang')
    const portuguese = new URLSearchParams(params)
    portuguese.set('lang', 'pt-BR')
    const english = new URLSearchParams(params)
    english.set('lang', 'en')
    const setAlternate = (hreflang: string, search: URLSearchParams) => {
      document
        .querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`)
        ?.setAttribute('href', `${location.pathname}?${search.toString()}`)
    }
    setAlternate('x-default', portuguese)
    setAlternate('pt-BR', portuguese)
    setAlternate('en', english)
  }, [i18n.resolvedLanguage, location.pathname, location.search, t])

  return (
    <div className="min-h-screen">
      <a href="#conteudo-principal" className="skip-link">{t('shell.skip')}</a>
      <div className="hidden lg:block">
        <Sidebar />
      </div>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            className="absolute inset-0 bg-black/45"
            aria-label={t('shell.closeOverlay')}
            onClick={() => setMobileOpen(false)}
          />
          <Sidebar onNavigate={() => setMobileOpen(false)} />
          <button
            type="button"
            className="absolute left-[252px] top-4 rounded-full bg-panel p-2 text-ink shadow-lg"
            onClick={() => setMobileOpen(false)}
            aria-label={t('shell.closeMenu')}
          >
            <X size={19} />
          </button>
        </div>
      )}

      <main id="conteudo-principal" tabIndex={-1} className="min-h-screen lg:ml-[244px]">
        <header className="brand-rule no-print sticky top-0 z-30 flex h-[66px] items-center justify-between bg-panel/92 px-4 backdrop-blur-xl sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="rounded-lg border border-line bg-panel-2 p-2 text-ink lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label={t('shell.openMenu')}
            >
              <Menu size={19} />
            </button>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-ink-3">{t('shell.platform')}</p>
              <p className="hidden text-sm font-semibold text-ink sm:block">{t('shell.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden xl:block">
              <StatusPill runId={run.data?.run_id} />
            </div>
            <ThemeSwitcher />
            <LanguageSwitcher />
          </div>
        </header>

        <div className="mx-auto w-full max-w-[1580px] p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
